import {backdropStyles} from "@components/Popup/styles";
import React, {PropsWithChildren, useRef} from "react";
import {Transition} from "react-transition-group";

import {useKeyHandler} from "../../../useEscape";
import {useFocusTrap} from "../../../utils/a11y/useFocusTrap";
import Fade from "../../animations/Fade";

/**
 * A little popup modal which slides out from the bottom of the screen.
 * Think of this as "a pop-up for mobile devices."
 */
const BottomSheet: React.FC<PropsWithChildren<Props>> = ({open, onClose, children}) => {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const setFocusTrap = useFocusTrap(open);
  useKeyHandler("Escape", onClose);

  return (
    <Fade in={open} mountOnEnter unmountOnExit>
      <div
        className={backdropStyles(open)}
        onPointerDown={onClose}
        data-testid="bottom-sheet-background"
      >
        <Transition nodeRef={nodeRef} in={open} timeout={100} mountOnEnter unmountOnExit>
          {state => {
            return (
              <div
                role="dialog"
                aria-modal
                className="relative mt-auto box-border rounded-t-3xl bg-white w-full bottom-0 z-50 transition transform duration-100"
                ref={el => {
                  setFocusTrap(el);
                  nodeRef.current = el;
                }}
                style={transitionStyles[state]}
                onPointerDown={e => {
                  e.stopPropagation();
                }}
              >
                {children}
              </div>
            );
          }}
        </Transition>
      </div>
    </Fade>
  );
};

const transitionStyles: Record<string, React.CSSProperties> = {
  entering: {transform: "translateY(0)"},
  entered: {transform: "translateY(0)"},
  exiting: {transform: "translateY(100%)"},
  exited: {transform: "translateY(100%)"},
  unmounted: {transform: "translateY(100%)"},
} as const;

export type Props = {
  /** Function which will be executed if the user closes the bottom sheet. */
  onClose: () => unknown;
  /** True if the bottom sheet is open, and false otherwise. */
  open: boolean;
};

export default React.memo(BottomSheet);
